import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  ENDPOINTS,
  NOTIFICATION_TIERS,
  RENEWAL_WINDOWS,
  type AnalyticsRenewalRow,
  type AnalyticsRenewalsDueResponse,
  type AnalyticsTierStatus,
  type NotificationTier,
  type CloudeqEntityKey,
  type Region,
  type RenewalWindow,
} from '@clm/shared-types';

import { fetchAnalyticsRenewalsDue } from '../../api/analytics';
import { ContractTypeTag } from '../../components/ContractTypeTag';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { ANALYTICS_FORBIDDEN_COPY } from '../analytics-shared';
import { PanelProblem } from '../quality/panel';
import { toProblem, type Panel } from '../quality/panel-state';
import { RENEWALS_EMPTY_COPY, TIER_LABELS, panelFromParams, panelToParam, windowFromParams } from './labels';

/**
 * The **Due for renewal** list under the strip — CLM-90 (D-90-3, D-90-9,
 * D-90-10).
 *
 * One row per in-force contract in the caller's scope whose due date —
 * `renewal_notice_date` when set, else `expiry_date`, the occurrence the
 * notifier itself watches — falls inside the window. Sorted by days left (the
 * API's order, O-6); past-due rows are in every window and sort first. Each row
 * carries the five tier cells of the notification ladder and a verification
 * caveat: *Unverified* while the feeding field is `Pending`, *Corrected to X*
 * when a reviewer corrected it and the record still reads the extracted date
 * (§1 item 5 of the spec — the write-back is a follow-up story).
 *
 * Sole owner of the `?window=` URL state (`useSearchParams`) and of its own
 * paging, so `AnalyticsDashboard.tsx` gains no hook (MERGE-ORDER, the CLM-91
 * seam). Pages append; the cursor is echoed verbatim.
 *
 * CLM-95 (D-95-3) — `AnalyticsDashboard.tsx` now owns *whether* this mounts at
 * all (the shared `?panel=` disclosure state) and this component gained one
 * prop, `onCollapse`, for the header's own collapse control; `?window=`
 * itself is still this component's own state, merged (never replaced) beside
 * `?panel=` so switching windows here does not close the section.
 */

const ENDPOINT_PATH = ENDPOINTS['analytics-renewals-due'].path;
const PANEL = 'Due for renewal';
const PAGE_LIMIT = 20;

const WINDOW_LABELS: Readonly<Record<RenewalWindow, string>> = {
  '30': '< 30 d',
  '60': '< 60 d',
  '90': '< 90 d',
  all: 'All',
  past_due: 'Past due',
};

function daysLeftCell(days: number) {
  if (days < 0) return <span className="tag bad">{String(-days)} d overdue</span>;
  return <span>{String(days)} d</span>;
}

function shortDate(iso: string): string {
  return iso.slice(0, 10);
}

function tierCell(status: AnalyticsTierStatus, tier: NotificationTier) {
  const label = TIER_LABELS[tier];
  switch (status.status) {
    case 'acknowledged':
      return (
        <span className="tag ok" aria-label={`${label}: acknowledged by ${status.acknowledged_by_upn ?? 'unknown'}`}>
          ack by {status.acknowledged_by_upn ?? '—'}
        </span>
      );
    case 'sent':
      return (
        <span className="tag" aria-label={`${label}: sent ${status.sent_utc === null ? '' : shortDate(status.sent_utc)}`}>
          ✓ sent {status.sent_utc === null ? '' : shortDate(status.sent_utc)}
        </span>
      );
    case 'not_sent':
      return (
        <span className="tag bad" aria-label={`${label}: not sent`}>
          not sent
        </span>
      );
    case 'not_due':
      return <span aria-label={`${label}: not due`}>—</span>;
  }
}

/**
 * The due date's verification caveat. CLM-108 (D-2.1-4, CR-06): an exhaustive
 * switch, so no status can fall through to "Verified" — before it, every
 * status other than the three named ones did, and `AutoApproved` would have
 * read as a person's check. "Verified" is printed only for a human settle of
 * the date itself, or — with no date row to read — for a contract whose
 * `data_trust` says every field was settled by a person.
 */
function verifiedCell(row: AnalyticsRenewalRow) {
  const verification = row.due_date_verification;
  if (verification === null) {
    if (row.data_trust === 'Verified') return <span>Verified</span>;
    if (row.data_trust === 'AutoApproved') return <span className="tag ai">Auto-approved</span>;
    return <span>Unverified</span>;
  }
  switch (verification.status) {
    case 'Pending':
      return <span className="tag">Unverified</span>;
    case 'Rejected':
      return <span className="tag bad">Rejected</span>;
    case 'AutoApproved':
      return <span className="tag ai">Auto-approved (policy)</span>;
    case 'Corrected':
      if (verification.confirmed_value !== null && verification.confirmed_value !== row.due_date) {
        return (
          <span className="tag bad">
            Corrected to {verification.confirmed_value} — the record still reads {row.due_date}
          </span>
        );
      }
      return <span>Verified</span>;
    case 'Verified':
      return <span>Verified</span>;
    default: {
      const exhaustive: never = verification.status;
      return exhaustive;
    }
  }
}

const COLUMNS: readonly DataTableColumn<AnalyticsRenewalRow>[] = [
  {
    id: 'contract',
    header: 'Contract',
    cell: (row) => (
      <>
        <Link to={`/contracts/record/${encodeURIComponent(row.contract_key)}`}>{row.contract_key}</Link>
        {row.sow_number === null ? null : <span className="mut"> {row.sow_number}</span>}
      </>
    ),
  },
  { id: 'client', header: 'Client', cell: (row) => row.client?.display_name ?? '—' },
  /* CLM-110 (R-110-23) — provisional until an administrator confirms it. */
  { id: 'type', header: 'Type', cell: (row) => <ContractTypeTag type={row.contract_type} confirmation={row.contract_type_confirmation} /> },
  { id: 'expiry_date', header: 'Expiry', cell: (row) => row.expiry_date ?? '—' },
  { id: 'renewal_notice_date', header: 'Renewal notice', cell: (row) => row.renewal_notice_date ?? '—' },
  {
    id: 'due',
    header: 'Due',
    cell: (row) => (
      <>
        {row.due_date} <span className="tag">{row.due_source === 'renewal_notice_date' ? 'renewal notice' : 'expiry'}</span>
      </>
    ),
  },
  { id: 'days_left', header: 'Days left', cell: (row) => daysLeftCell(row.days_left), cellClassName: 'num' },
  ...NOTIFICATION_TIERS.map(
    (tier): DataTableColumn<AnalyticsRenewalRow> => ({
      id: tier,
      header: TIER_LABELS[tier],
      cell: (row) => {
        const status = row.notifications.find((candidate) => candidate.tier === tier);
        return status === undefined ? '—' : tierCell(status, tier);
      },
    }),
  ),
  { id: 'verified', header: 'Verified', cell: (row) => verifiedCell(row) },
];

type ListState = Panel<AnalyticsRenewalsDueResponse>;

export function RenewalsDueList({
  partyKey,
  region,
  entityKey,
  onCollapse,
}: {
  readonly partyKey: string | null;
  readonly region: Region | null;
  /** CLM-91 (D-91-5) — the signing cloudEQ entity; the same narrow the dashboard bound, so the strip and the list agree (D-90-4). */
  readonly entityKey: CloudeqEntityKey | null;
  /** CLM-95 (D-95-3) — the section's own collapse control; clears the shared `?panel=` state. */
  readonly onCollapse: () => void;
}) {
  const [params, setParams] = useSearchParams();
  // `?panel=due-<window>` is the value AttentionStrip's `aria-expanded` tile
  // state renders from (this component only mounts while it decodes to a
  // `due` panel), so it — not the echoed `?window=` — is the source of truth
  // on mount/reload/share; a mismatched or absent `?window=` would otherwise
  // show a different window than the tile claims is open (CLM-95).
  const panelFromUrl = panelFromParams(params.get('panel'));
  const window = panelFromUrl?.kind === 'due' ? panelFromUrl.window : windowFromParams(params.get('window'));
  const onWindow = useCallback(
    (next: RenewalWindow) => {
      // Merge, never replace (D-95-3): a plain-object setParams would drop
      // `?panel=`, silently collapsing the section the window switch happens
      // inside of. `panel` is also KEPT IN SYNC with the window, not just
      // preserved: this component only ever mounts while a due-* panel is
      // active, so leaving the old panel id in place after switching windows
      // would show the wrong AttentionStrip tile as expanded, and corrupt a
      // shared link (it would name a different window than the list renders).
      setParams((previous) => {
        const merged = new URLSearchParams(previous);
        if (next === '90') merged.delete('window');
        else merged.set('window', next);
        merged.set('panel', panelToParam({ kind: 'due', window: next }));
        return merged;
      });
    },
    [setParams],
  );

  const [state, setState] = useState<ListState>({ kind: 'loading' });
  const [items, setItems] = useState<readonly AnalyticsRenewalRow[]>([]);
  /* The cursor is keyed on the filters it was issued for: a filter change makes it null in the SAME render,
   * so the fetch below never fires once with a stale cursor and the new window (the API would answer 400
   * `analytics.cursor_rejected` — the fingerprint no longer matches). A page appends; a filter change resets. */
  const filterKey = `${partyKey ?? ''}|${region ?? ''}|${entityKey ?? ''}|${window}`;
  const [page, setPage] = useState<{ readonly cursor: string; readonly forFilters: string } | null>(null);
  const cursor = page !== null && page.forFilters === filterKey ? page.cursor : null;
  const [nonce, setNonce] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    if (cursor === null) {
      setItems([]);
      setState({ kind: 'loading' });
    } else {
      setLoadingMore(true);
    }

    void (async () => {
      try {
        const response = await fetchAnalyticsRenewalsDue(
          {
            ...(partyKey === null ? {} : { party_key: partyKey }),
            ...(region === null ? {} : { region }),
            ...(entityKey === null ? {} : { cloudeq_entity: entityKey }),
            window,
            page: { limit: PAGE_LIMIT, ...(cursor === null ? {} : { cursor }) },
          },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setItems((previous) => (cursor === null ? response.items : [...previous, ...response.items]));
        setState({ kind: 'ready', response });
      } catch (error) {
        if (controller.signal.aborted) return;
        setState(toProblem(error));
      } finally {
        if (!controller.signal.aborted) setLoadingMore(false);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [partyKey, region, entityKey, window, cursor, nonce]);

  const reload = useCallback(() => {
    setPage(null);
    setNonce((n) => n + 1);
  }, []);

  const nextCursor = state.kind === 'ready' ? state.response.page.next_cursor : null;

  return (
    <section className="card analytics-section renewals" aria-labelledby="renewals-heading">
      <div className="analytics-head">
        <h3 id="renewals-heading">Due for renewal</h3>
        <fieldset className="window-filter">
          <legend className="vh">Window</legend>
          {RENEWAL_WINDOWS.map((candidate) => (
            <label key={candidate}>
              <input
                type="radio"
                name="renewals-window"
                value={candidate}
                checked={window === candidate}
                onChange={() => {
                  onWindow(candidate);
                }}
              />{' '}
              {WINDOW_LABELS[candidate]}
            </label>
          ))}
        </fieldset>
        {state.kind === 'ready' ? (
          <p className="mut">
            {String(state.response.page.total_in_scope ?? items.length)} in scope · sorted by days left
          </p>
        ) : null}
        <button type="button" className="btn sm" onClick={onCollapse}>
          Collapse
        </button>
      </div>

      {state.kind === 'loading' ? (
        <p role="status" aria-busy="true">
          Reading {PANEL}…
        </p>
      ) : null}

      {state.kind === 'problem' ? (
        <PanelProblem panel={PANEL} state={state} endpointPath={ENDPOINT_PATH} onRetry={reload} forbiddenCopy={ANALYTICS_FORBIDDEN_COPY} />
      ) : null}

      {state.kind === 'ready' ? (
        <>
          <DataTable
            caption={`Contracts due for renewal, ${WINDOW_LABELS[window]}`}
            columns={COLUMNS}
            rows={items}
            rowKey={(row) => row.contract_key}
            busy={loadingMore}
            empty={<p className="mut">{RENEWALS_EMPTY_COPY}</p>}
          />
          {nextCursor === null ? null : (
            <button
              type="button"
              className="btn"
              disabled={loadingMore}
              onClick={() => {
                setPage({ cursor: nextCursor, forFilters: filterKey });
              }}
            >
              Load more
            </button>
          )}
        </>
      ) : null}
    </section>
  );
}
